import { useEffect, useRef } from "react";
import {
  useMutation,
  useQuery,
  useQueryClient,
  type InfiniteData,
  type QueryClient,
} from "@tanstack/react-query";
import { useAuth } from "@workos-inc/authkit-react";
import * as Sentry from "@sentry/react";

import { ApiError, apiFetchMultipart } from "@/lib/api";
import {
  apiErrorBodyForCapture,
  buildAppendFormData,
  isRetryable,
  outboxBackoff,
  outboxFingerprint,
} from "@/lib/outbox";
import { planKeys, projectKeys, recordKeys } from "@/lib/query-keys";
import { invalidateReportContents } from "@/lib/invalidate-report-contents";
import {
  deletePendingAppend,
  getAllPendingAppends,
  updatePendingAppendStatus,
  type PendingAppend,
} from "@/offline/db";
import { markRecordJustSynced } from "@/hooks/use-just-synced";
import { normalizeRecordImageShape } from "@/offline/record-shape";
import type { PaginatedResponse, ProjectRecord } from "@/types/api";

/**
 * Variables for one append attempt — the whole entry.
 *
 * Unlike a record-create (which carries only the `clientUuid` and re-reads the
 * outbox each attempt because the entry can be edited or deleted mid-upload),
 * an append is immutable once queued: it is add-only, has no edit/delete
 * lifecycle, and no temporary-id handoff. So the entry is captured into the
 * mutation variables and reused across retries — no per-attempt IDB re-read.
 */
interface AppendVars {
  entry: PendingAppend;
}

/**
 * Reconcile an appended photo into the project's record caches straight from
 * the append response — replace the record (by id) wherever it appears, in the
 * lists and in its own single-record entry, with the server's updated version,
 * whose `images[]` now carries the new photo and every photo's
 * `client_photo_uuid`. A SIBLING of
 * `insertSyncedRecordIntoListCaches` in {@link import("./use-outbox-drain")}:
 * that one PREPENDS a freshly-created record; this one REPLACES an existing one
 * in place, so it needs no filter-matching — the record is already in the view,
 * and the background refetch reconciles any filter/order drift.
 *
 * Only ACTIVE queries are touched (the same rule the create drain uses), so
 * an inactive cache is left for its own refetch on remount. Doing this
 * synchronously — not via an invalidate→refetch round-trip — is what lets the
 * open viewer's LIVE record hold the server photo the instant the optimistic
 * tile is retired, with no in-between frame. See [[append-photo-outbox]] § Success.
 */
function updateRecordInCaches(
  queryClient: QueryClient,
  projectId: string,
  record: ProjectRecord,
): void {
  const queries = queryClient.getQueryCache().findAll({
    queryKey: recordKeys.byProject(projectId),
    type: "active",
  });
  for (const query of queries) {
    const key = query.queryKey;
    // recordKeys.detail(...) is ["records", projectId, "detail", recordId]: the
    // record read on its own, as a photo opened from a report is. Replaced
    // whole, for the same no-gap reason as the lists.
    if (key[2] === "detail") {
      if (key[3] === record.id) queryClient.setQueryData(key, record);
      continue;
    }
    // recordKeys.list(...) is ["records", projectId, "list", filters].
    if (key[2] !== "list") continue;
    queryClient.setQueryData<InfiniteData<PaginatedResponse<ProjectRecord>>>(
      key,
      (old) => {
        if (!old) return old;
        let changed = false;
        const pages = old.pages.map((page) => {
          if (!page.results.some((r) => r.id === record.id)) return page;
          changed = true;
          return {
            ...page,
            results: page.results.map((r) =>
              r.id === record.id ? record : r,
            ),
          };
        });
        return changed ? { ...old, pages } : old;
      },
    );
  }
}

/**
 * Background sync engine for the offline APPEND outbox.
 *
 * A single always-mounted hook that drains the durable `pendingAppends`
 * IndexedDB store: it uploads every queued photo to the server's v2.0 record
 * endpoint, retries transient failures forever with capped exponential
 * backoff, and pauses while offline (auto-resuming on reconnect/refocus). It
 * is a deliberate SIBLING of {@link import("./use-outbox-drain").useOutboxDrain}
 * — same retry/idempotency discipline — but a far simpler lifecycle: an append
 * augments an existing, synced record, so there is no temporary-id handoff, no
 * edit/delete reconcile, no `deleted` flag, and no reconcile-on-boot. See
 * [[append-photo-outbox]].
 *
 * Lifecycle of one queued photo:
 *   1. The drain effect sees a `pending` entry not already in-flight and fires
 *      the uploader mutation, recording its `photoId` in a ref Set so the same
 *      photo is never fired twice when the effect re-runs.
 *   2. `mutationFn` POSTs the multipart body with `X-Idempotency-Key:
 *      <photoId>` so retries can never attach the photo twice — the same
 *      endpoint + header the online append in `use-append-photo.ts` uses.
 *   3. `retry`/`retryDelay` keep transient failures retrying forever;
 *      `networkMode: "online"` pauses the whole mutation while offline instead
 *      of burning a permanent failure.
 *   4. `onSuccess` deletes the outbox entry, invalidates the same
 *      record/project/plan queries the online append invalidates (so the
 *      record refetches with the real photo), invalidates `["pendingAppends"]`,
 *      and flags the record just-synced for the brief grid-tile confirmation.
 *   5. `onError` (only reached after `retry` permanently gives up on a 4xx)
 *      marks the entry `failed` so the record's tile shows an error badge and
 *      the failed photo carries a tap-to-retry inside the viewer.
 */
export function useAppendDrain(): void {
  const queryClient = useQueryClient();
  const { getAccessToken } = useAuth();

  // `photoId`s with an upload mutation currently in flight. A ref (not state)
  // so adding/removing an entry never triggers a re-render — the drain effect
  // re-reads it synchronously when the pending list changes.
  const inFlightRef = useRef<Set<string>>(new Set());

  // Observe the WHOLE append outbox (every project/record). Invalidating the
  // `["pendingAppends"]` prefix — which the modal's queue-and-close, the
  // drain, and tap-to-retry all do — refetches this query, so a freshly
  // queued or re-queued photo is replayed automatically; on a fresh boot the
  // initial fetch replays any appends left pending from a prior session.
  const { data: pendingAppends } = useQuery({
    queryKey: ["pendingAppends"],
    queryFn: getAllPendingAppends,
    // The append outbox is local IndexedDB; only the modal/drain/retry mutate
    // it and they invalidate this key explicitly. No background refetch churn.
    staleTime: Infinity,
    gcTime: 0,
  });

  const uploader = useMutation<ProjectRecord, Error, AppendVars>({
    mutationKey: ["appendOutbox", "upload"],
    // Pause (don't fail) the whole mutation while offline; TanStack's
    // QueryClient auto-resumes paused mutations on reconnect/refocus.
    networkMode: "online",
    // Retry-forever for transient failures; a permanent 4xx stops here.
    retry: (_failureCount, error) => isRetryable(error),
    retryDelay: outboxBackoff,
    mutationFn: ({ entry }): Promise<ProjectRecord> =>
      apiFetchMultipart<ProjectRecord>(
        `/projects/2.0/projects/${entry.projectId}/records/`,
        buildAppendFormData(entry),
        getAccessToken,
        {
          method: "POST",
          // Per-photo idempotency key = the photo's client_photo_uuid.
          headers: { "X-Idempotency-Key": entry.photoId },
        },
      ),
    onSuccess: async (record, { entry }) => {
      const { photoId, projectId, recordId } = entry;

      // Reconcile the record's canonical photo list straight from the append
      // response — the whole updated record, its `images[]` carrying the new
      // photo and every photo's `client_photo_uuid` — and do it BEFORE retiring
      // the optimistic entry. The open viewer reads the LIVE record, so its
      // server photo is in place the instant the optimistic tile is dropped:
      // no frame where the photo belongs to neither. Normalize to the canonical
      // image shape first, exactly as `use-append-photo.ts` does, so the spliced
      // record matches every other list reader. See [[append-photo-outbox]] § Success.
      updateRecordInCaches(
        queryClient,
        projectId,
        normalizeRecordImageShape(record),
      );

      // Now drop the append entry — its server twin is already in the cache, so
      // the viewer dedups the optimistic tile away rather than losing the photo.
      await deletePendingAppend(photoId);

      // Background reconcile: the SAME record/project/plan queries
      // `use-append-photo.ts` invalidates (ordering, counts, sibling caches),
      // plus the outbox key. The list cache already shows the photo via the
      // reconcile above; this just confirms it against the server.
      void queryClient.invalidateQueries({
        queryKey: recordKeys.byProject(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: projectKeys.detail(projectId),
      });
      void queryClient.invalidateQueries({
        queryKey: planKeys.byProject(projectId),
      });
      // A photos report counts photos, and its tile shows the record's count.
      invalidateReportContents(queryClient, projectId, { counts: true });
      void queryClient.invalidateQueries({ queryKey: ["pendingAppends"] });

      // Brief "Synced" confirmation on the record's grid tile, keyed on the
      // real record id — the record is real and on screen, unlike a creating
      // record whose tile is synthesized.
      markRecordJustSynced(recordId);
    },
    onError: (error, { entry }) => {
      // Reached only when `retry` permanently gave up — a 4xx the server
      // actively rejected. Mark the entry `failed` so the record's tile shows
      // an error badge and the failed photo carries a tap-to-retry inside the
      // viewer. Never a popup. A transient failure never lands here; it keeps
      // retrying. Transient append failures are expected and NOT reported —
      // but a PERMANENT one must reach ops: the server refused a queued photo
      // nobody is watching (see [[error-reporting]] § Offline and outbox
      // errors). Metadata only — ids, status, the server's reason.
      Sentry.captureException(error, {
        level: "error",
        tags: { area: "outbox", phase: "append-permanent-failure" },
        fingerprint: outboxFingerprint("append-permanent-failure", error),
        contexts: {
          outbox: {
            photoId: entry.photoId,
            recordId: entry.recordId,
            projectId: entry.projectId,
            status: error instanceof ApiError ? error.status : undefined,
            body: apiErrorBodyForCapture(error),
          },
        },
      });
      void updatePendingAppendStatus(entry.photoId, "failed", error.message).then(
        () => {
          void queryClient.invalidateQueries({ queryKey: ["pendingAppends"] });
        },
      );
    },
    onSettled: (_data, _error, { entry }) => {
      // Whatever the outcome, this upload is no longer in flight — clear the
      // guard so the drain effect may fire it again (e.g. tap-to-retry).
      inFlightRef.current.delete(entry.photoId);
    },
  });

  // The drain loop. For every `pending` append not already in flight, fire the
  // upload. Adding the photoId to the in-flight Set BEFORE calling `mutate`
  // guards against the same photo being fired twice when this effect re-runs
  // (e.g. the pending list refetches mid-upload). `failed` and in-flight
  // entries are skipped.
  useEffect(() => {
    if (!pendingAppends) return;
    for (const entry of pendingAppends) {
      if (entry.status !== "pending") continue;
      if (inFlightRef.current.has(entry.photoId)) continue;
      inFlightRef.current.add(entry.photoId);
      uploader.mutate({ entry });
    }
  }, [pendingAppends, uploader]);
}
